import type { AppRegistryStatus } from '@clarix/shared-types';

/**
 * Registry statuses an app can actually be opened in. The App registry
 * (/admin → App registry) is the single source of truth for this: flipping an
 * app to COMING_SOON there must remove it from the AppSwitcher/launcher AND
 * close its routes, in every workspace that has it enabled.
 *
 * BETA counts as launchable — a beta app still ships, it is just gated by which
 * workspaces have it enabled.
 */
const LAUNCHABLE_REGISTRY_STATUSES: readonly AppRegistryStatus[] = ['ACTIVE', 'BETA'];

/**
 * Whether an app is launchable, given its /me app-access entry.
 *
 * Two independent gates, both of which must pass:
 *   1. `status`          — this member's access in this workspace (the backend
 *                          already folds a workspace-level PAUSE/SUSPEND into it).
 *   2. `registry_status` — whether the app is shipped platform-wide.
 *
 * A missing `registry_status` means the backend predates the field, so it is
 * treated as launchable rather than locking every app out on a version skew.
 */
export function isAppLaunchable(
  entry: { status?: string; registry_status?: string } | undefined,
): boolean {
  if (entry?.status !== 'ACTIVE') return false;
  if (!entry.registry_status) return true;
  return LAUNCHABLE_REGISTRY_STATUSES.includes(entry.registry_status as AppRegistryStatus);
}
